import { useEffect, useState } from 'react'
import { Grid, Paper, Typography, Box, Tooltip, IconButton, useTheme } from '@mui/material'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import supabase from '../supabaseClient'
import { SectionHeader } from './ui/StyledComponents'

function QuranWeeklyProgress({ studentId, classId, selectedDate }) {
  const [weeklyProgress, setWeeklyProgress] = useState([])
  const [weekOffset, setWeekOffset] = useState(0)
  const theme = useTheme()

  const hasValidQuranProgress = (progress) => {
    return progress?.starting_surah && 
           progress?.starting_verse && 
           progress?.ending_surah && 
           progress?.ending_verse
  }

  useEffect(() => {
    const fetchWeeklyProgress = async () => {
      // Calculate dates inside the effect, accounting for week offset
      const endDate = selectedDate.clone().subtract(1 + (weekOffset * 7), 'day')
      const startDate = selectedDate.clone().subtract(7 + (weekOffset * 7), 'day')

      const { data, error } = await supabase
        .from('student_quran_progress')
        .select('*')
        .eq('student_id', studentId)
        .eq('class_id', classId)
        .gte('date', startDate.format('YYYY-MM-DD'))
        .lte('date', endDate.format('YYYY-MM-DD'))
        .order('date', { ascending: true })

      if (error) {
        console.error('Error fetching weekly progress:', error)
        return
      }

      const progressByDay = []
      let currentDate = startDate.clone()
      
      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        const dayProgress = data.find(d => d.date === dateStr) || null
        progressByDay.push({
          date: currentDate.clone(),
          progress: dayProgress,
          isSelected: false
        })
        currentDate = currentDate.add(1, 'day')
      }

      setWeeklyProgress(progressByDay)
    }

    if (studentId && classId && selectedDate) {
      fetchWeeklyProgress()
    }
  }, [studentId, classId, selectedDate, weekOffset])

  const handlePreviousWeek = () => {
    setWeekOffset(prev => prev + 1)
  }

  const handleNextWeek = () => {
    setWeekOffset(prev => Math.max(0, prev - 1))
  }

  const getStatusColor = (progress) => {
    const isDark = theme.palette.mode === 'dark'
    
    if (!progress) return isDark ? '#2a2a2a' : '#f5f5f5' // grey for no data
    if (progress.assignment_status === 'full') return isDark ? '#1e4a1e' : '#e8f5e9' // green
    if (progress.assignment_status === 'partial') return isDark ? '#4a2e1e' : '#fff3e0' // orange
    return isDark ? '#4a1e1e' : '#ffebee' // red
  }

  const getBorderColor = (progress, isSelected) => {
    const isDark = theme.palette.mode === 'dark'
    
    if (isSelected) return theme.palette.primary.main // theme primary color
    if (!progress) return isDark ? '#555' : '#e0e0e0' // grey for no data
    if (progress.assignment_status === 'full') return isDark ? '#66bb6a' : '#4caf50' // green
    if (progress.assignment_status === 'partial') return isDark ? '#ffb74d' : '#ff9800' // orange
    return isDark ? '#ef5350' : '#ef5350' // red (same for both modes)
  }

  return (
    <Box sx={{ mb: { xs: 2, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        justifyContent: 'space-between',
        mb: { xs: 1.5, sm: 2 },
        gap: { xs: 1.5, sm: 0 },
      }}>
        <SectionHeader sx={{ 
          mb: 0,
          fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
        }}>
          Previous Week ({selectedDate?.clone().subtract(7 + (weekOffset * 7), 'day').format('MMM D')} - {selectedDate?.clone().subtract(1 + (weekOffset * 7), 'day').format('MMM D')})
        </SectionHeader>
        <IconButton
          onClick={() => setWeekOffset(0)}
          disabled={weekOffset === 0}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(144, 202, 249, 0.08)' 
              : 'rgba(25, 118, 210, 0.04)',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(144, 202, 249, 0.3)'
              : '1px solid rgba(25, 118, 210, 0.2)',
            color: theme.palette.primary.main,
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.75, sm: 1 },
            borderRadius: 1,
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(144, 202, 249, 0.16)'
                : 'rgba(25, 118, 210, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(144, 202, 249, 0.5)'
                : '1px solid rgba(25, 118, 210, 0.5)',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.04)'
                : 'rgba(0, 0, 0, 0.04)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.12)'
                : '1px solid rgba(0, 0, 0, 0.12)',
              color: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.26)'
                : 'rgba(0, 0, 0, 0.26)'
            },
            '& .MuiTypography-root': {
              ml: { xs: 0.5, sm: 1 },
              fontSize: { xs: '0.7rem', sm: '0.875rem' },
            }
          }}
        >
          <Typography variant="button">This Week</Typography>
        </IconButton>
      </Box>
      <Paper 
        elevation={2} 
        sx={{ 
          p: { xs: 1, sm: 1.5, md: 2 },
          backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f0f0f0',
          borderRadius: 2,
          border: `1px solid ${theme.palette.primary.main}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handlePreviousWeek}
            sx={{ 
              mr: { xs: 0.5, sm: 1 },
              height: { xs: '40px', sm: '45px', md: '50px' },
              width: { xs: '40px', sm: '45px', md: '50px' },
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(144, 202, 249, 0.08)'
                : 'rgba(25, 118, 210, 0.04)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(144, 202, 249, 0.3)'
                : '1px solid rgba(25, 118, 210, 0.2)',
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.16)'
                  : 'rgba(25, 118, 210, 0.1)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(144, 202, 249, 0.5)'
                  : '1px solid rgba(25, 118, 210, 0.5)',
              }
            }}
          >
            <ChevronLeft sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }} />
          </IconButton>

          <Box sx={{ 
            flex: 1, 
            overflowX: { xs: 'auto', sm: 'visible' },
            '&::-webkit-scrollbar': {
              height: { xs: '6px', sm: '0px' },
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
            },
          }}>
            <Grid container spacing={{ xs: 0.5, sm: 1 }} sx={{ minWidth: { xs: '560px', sm: 'auto' } }}>
              {weeklyProgress.map((day, index) => (
                <Grid item xs key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1, sm: 1.25, md: 1.5 },
                      backgroundColor: getStatusColor(day.progress),
                      border: 2,
                      borderColor: getBorderColor(day.progress, day.isSelected),
                      borderRadius: 1,
                      height: { xs: '150px', sm: '175px', md: '200px' },
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      overflow: 'hidden', // Prevent content overflow
                    }}
                  >
                    {/* Day of Week Badge */}
                    <Box 
                      sx={{
                        position: 'absolute',
                        top: { xs: 2, sm: 3, md: 4 },
                        left: { xs: 2, sm: 3, md: 4 },
                        backgroundColor: day.progress 
                          ? getBorderColor(day.progress) 
                          : '#9e9e9e',
                        color: 'white',
                        borderRadius: '4px',
                        padding: { xs: '1px 4px', sm: '2px 5px', md: '2px 6px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                          fontWeight: 'medium',
                        }}
                      >
                        {day.date.format('ddd')}
                      </Typography>
                    </Box>

                    {/* Date Badge */}
                    <Box 
                      sx={{
                        position: 'absolute',
                        top: { xs: 2, sm: 3, md: 4 },
                        right: { xs: 2, sm: 3, md: 4 },
                        backgroundColor: day.progress 
                          ? getBorderColor(day.progress) 
                          : '#9e9e9e',
                        color: 'white',
                        borderRadius: '4px',
                        padding: { xs: '1px 4px', sm: '2px 5px', md: '2px 6px' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        sx={{
                          fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                          fontWeight: 'medium',
                        }}
                      >
                        {day.date.format('MMM D')}
                      </Typography>
                    </Box>

                    {/* Progress Content */}
                    <Box 
                      sx={{ 
                        mt: { xs: 3, sm: 3.5, md: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%' // Take up remaining space
                      }}
                    >
                      {/* Quran Progress Section */}
                      <Box 
                        sx={{ 
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          px: { xs: 0.5, sm: 0.75, md: 1 },
                          textAlign: 'center' // Center align text
                        }}
                      >
                        {day.progress && hasValidQuranProgress(day.progress) ? (
                          <>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 'medium',
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                lineHeight: 1.2,
                                maxWidth: '100%', // Limit width
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Surah {day.progress.starting_surah}:{day.progress.starting_verse}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mt: { xs: 0.25, sm: 0.5 },
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                lineHeight: 1.2
                              }}
                            >
                              to
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                mt: { xs: 0.25, sm: 0.5 },
                                fontWeight: 'medium',
                                color: theme.palette.text.secondary,
                                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                lineHeight: 1.2
                              }}
                            >
                              {day.progress.ending_surah}:{day.progress.ending_verse}
                            </Typography>
                          </>
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.text.disabled,
                              fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                              fontStyle: 'italic'
                            }}
                          >
                            No Data
                          </Typography>
                        )}
                      </Box>

                      {/* Notes Section */}
                      <Box 
                        sx={{ 
                          borderTop: theme.palette.mode === 'dark' 
                            ? '1px solid #555' 
                            : '1px solid #ccc',
                          pt: { xs: 0.75, sm: 1 },
                          px: { xs: 0.5, sm: 0.75, md: 1 },
                          minHeight: { xs: '30px', sm: '35px', md: '40px' },
                          maxHeight: { xs: '40px', sm: '45px', md: '50px' },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden' // Prevent overflow
                        }}
                      >
                        <Tooltip 
                          title={day.progress?.notes || 'No Notes Added'}
                          enterDelay={500}
                          enterNextDelay={500}
                          placement="bottom"
                          sx={{
                            maxWidth: { xs: '200px', sm: '250px', md: '300px' }
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: day.progress 
                                ? theme.palette.text.secondary 
                                : theme.palette.text.disabled,
                              fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                              fontStyle: 'italic',
                              lineHeight: 1.3,
                              display: '-webkit-box',
                              WebkitLineClamp: { xs: 2, sm: 3 },
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer' // Indicate that hovering will do something
                            }}
                          >
                            {(day.progress && day.progress.notes) || 'No Notes Added'}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          <IconButton 
            onClick={handleNextWeek}
            disabled={weekOffset === 0}
            sx={{ 
              ml: { xs: 0.5, sm: 1 },
              height: { xs: '40px', sm: '45px', md: '50px' },
              width: { xs: '40px', sm: '45px', md: '50px' },
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(144, 202, 249, 0.08)'
                : 'rgba(25, 118, 210, 0.04)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(144, 202, 249, 0.3)'
                : '1px solid rgba(25, 118, 210, 0.2)',
              color: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.16)'
                  : 'rgba(25, 118, 210, 0.1)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(144, 202, 249, 0.5)'
                  : '1px solid rgba(25, 118, 210, 0.5)',
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.04)',
                border: theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.12)'
                  : '1px solid rgba(0, 0, 0, 0.12)',
                color: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.26)'
                  : 'rgba(0, 0, 0, 0.26)'
              }
            }}
          >
            <ChevronRight sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  )
}

export default QuranWeeklyProgress 